define([
  'react',
  'immutable',
  'underscore',
  'es6!sumo/util',
  'es6!sumo/util/Font',
  'es6!launcher/util/listbuilder/wysiwyg/constants',
  'es6!launcher/util/listbuilder/wysiwyg/util',
  'es6!launcher/util/listbuilder/wysiwyg/dom',
  'es6!launcher/util/listbuilder/constants',
  'jsx!launcher/views/listbuilder/wysiwyg/components/ResizeHandle',
  'jsx!launcher/views/listbuilder/wysiwyg/components/MoveHandle',
], (React, Immutable, _, Util, FontUtil, WysiwygConstants, WysiwygUtil, WysiwygDom, Constants, ResizeHandle, MoveHandle) => {
  const {
    MINIMUM_COMPONENT_SIZE,
    RESIZE_HANDLE_SIZE,
  } = WysiwygConstants;
  const COMPONENT_CLASS_NAME = 'sumome-react-wysiwyg-component';
  const VERTICAL_HANDLES = ['top', 'bottom'];
  const HORIZONTAL_HANDLES = ['left', 'right'];
  const SIDE_HANDLES = [].concat(VERTICAL_HANDLES, HORIZONTAL_HANDLES);
  const CORNER_HANDLES = ['top-left', 'top-right', 'bottom-left', 'bottom-right']
  const HANDLES = [].concat(
    VERTICAL_HANDLES,
    HORIZONTAL_HANDLES,
    CORNER_HANDLES
  );

  const classNames = Util.classNames;
  const {
    getRoundedPosition,
    styleToPosition,
    positionToStyle,
    isInlineComponent,
  } = WysiwygUtil;

  const { FormTypes } = Constants;

  function handleNameToPascalCase(handle) {
    if (!handle || !handle.length) return '';

    return _.reduce(handle.split('-'), (memo, handlePart) => {
      // Convoluted, but capitalize first char and concatenate each word.
      return `${memo}${handlePart.charAt(0).toUpperCase()}${handlePart.slice(1)}`;
    }, '');
  }

  /**
   * Returns a class name for the given responsive hidden property
   */
  function getClassNameForResponsiveHiddenOn (responsiveHiddenOn) {
    let className = [];
    responsiveHiddenOn.forEach((hidden, size) => {
      if (hidden) {
        className.push(`sumome-wysiwyg-hidden-${size}`);
      }
    });
    return className.join(' ');
  }

  return (options) => {
    options = options || {};
    const {
      disableVerticalResize,
      disableHorizontalResize,
    } = options;

    // If either horizontal or vertical resize is disabled, we can't have corner resizing.
    const disableCornerResize = disableHorizontalResize || disableVerticalResize;

    function validatePosition(position) {
      if (!disableHorizontalResize && position.width < MINIMUM_COMPONENT_SIZE) {
        position.width = MINIMUM_COMPONENT_SIZE;
      }
      if (!disableVerticalResize && position.height < MINIMUM_COMPONENT_SIZE) {
        position.height = MINIMUM_COMPONENT_SIZE;
      }
      return position;
    }

    // For corner resizing, we keep track of the initial position of the mouse and the
    // original position and size of the component to calculate diffs from initial.
    // Deltas don't work due to rounding error and our resizing doesn't match our
    // mouse movement since it is proportional resizing.
    function getCornerDragDeltas(side, ui, initialSize) {
      if (CORNER_HANDLES.indexOf(side) < 0) return null;
      if (!ui || !initialSize || !initialSize.width || !initialSize.height) return null;
      const { x, y, initialX, initialY } = ui;
      const deltaX = x - initialX;
      const deltaY = y - initialY;
      const aspectRatio = initialSize.width / initialSize.height;

      let correctedX;
      let correctedY;
      // For each side, positive is in a different direction. The switch block corrects it.
      switch (side) {
        case 'top-left': {
          correctedX = -deltaX;
          correctedY = -deltaY;
          break;
        }
        case 'top-right': {
          correctedX = deltaX;
          correctedY = -deltaY;
          break;
        }
        case 'bottom-left': {
          correctedX = -deltaX;
          correctedY = deltaY;
          break;
        }
        case 'bottom-right': {
          correctedX = deltaX;
          correctedY = deltaY;
          break;
        }
      }

      let deltaWidth;
      let deltaHeight;
      // Normalize the offset from initial position by turning deltaY into it's
      // proportional width and then comparing widths.
      if (correctedX > correctedY * aspectRatio) {
        deltaWidth = correctedX;
        deltaHeight = correctedX * (1 / aspectRatio);
      } else {
        deltaHeight = correctedY;
        deltaWidth = correctedY * aspectRatio;
      }

      return { x: deltaWidth, y: deltaHeight };
    }

    return (ChildComponent) => class Component extends React.Component {
      static defaultProps = {
        style: Immutable.Map(),
        components: Immutable.List(),
        isActiveDropTarget: false,
      };

      constructor(props) {
        super(props);

        const {
          id,
          style,
          onSelectComponent,
          onMouseEnterComponent,
          onMouseLeaveComponent,
          onFocusFieldComponent,
          type,
          position,
        } = props;
        const respectPercentages = props.formType === FormTypes.WELCOME_MAT;

        this.state = _.extend(styleToPosition(style.toJS(), respectPercentages), {
          isSnapping: true,
          isMoveLockedToAxis: false,
          isMoving: false,
          isResizingSide: false,
          isResizingCorner: false,
          inputField: null,
        });

        this.updateComponentPosition = this.updateComponentPosition.bind(this);

        this.onSaveInputField = this.onSaveInputField.bind(this);

        this.onKeyDown = this.onKeyDown.bind(this);
        this.onKeyUp = this.onKeyUp.bind(this);
        this.onPressArrowKey = this.onPressArrowKey.bind(this);

        this.onStopDraggingHandle = this.onStopDraggingHandle.bind(this);

        this.onStartDraggingMoveHandle = this.onStartDrag.bind(this, 'move');
        _.each(HANDLES, handle => {
          const handlePascalCase = handleNameToPascalCase(handle);
          this[`onStartDragging${handlePascalCase}Handle`] = this.onStartDrag.bind(this, handle);
        });

        this.saveHeight = this.saveHeight.bind(this);
        this.onSaveAspectRatio = this.onSaveAspectRatio.bind(this);

        this.onDragMoveHandle = this.onDragMoveHandle.bind(this);
        this.onDragTopHandle = this.onDragSideHandle.bind(this, 'top');
        this.onDragBottomHandle = this.onDragSideHandle.bind(this, 'bottom');
        this.onDragRightHandle = this.onDragSideHandle.bind(this, 'right');
        this.onDragLeftHandle = this.onDragSideHandle.bind(this, 'left');
        this.onDragTopLeftHandle = this.onDragCornerHandle.bind(this, 'top-left');
        this.onDragTopRightHandle = this.onDragCornerHandle.bind(this, 'top-right');
        this.onDragBottomLeftHandle = this.onDragCornerHandle.bind(this, 'bottom-left');
        this.onDragBottomRightHandle = this.onDragCornerHandle.bind(this, 'bottom-right');

        this.debounceOnChangeComponent = props.onChangeComponent ? _.debounce(props.onChangeComponent, 25) : null;

        if (onSelectComponent) this.onSelectComponent = onSelectComponent.bind(this, id);
        if (onMouseEnterComponent) this.onMouseEnter = onMouseEnterComponent.bind(this, id);
        if (onMouseLeaveComponent) this.onMouseLeave = onMouseLeaveComponent.bind(this, id);
        if (onFocusFieldComponent) {
          this.onFocus = onFocusFieldComponent.bind(this, id, false);
          this.onClickErrorTooltip = this.onClickErrorTooltip.bind(this);
          this.onBlur = onFocusFieldComponent.bind(this, id, true);
        }
      }

      // Only update local state if not moving or resizing.  The component tracks it's own state when
      // moving and resizing. This is for performance since it doesn't have to hit the store everytime.
      componentWillReceiveProps(nextProps) {
        const { style, type, action, formType, id, onChangeComponent } = nextProps;
        const { isMoving } = this.state;
        const respectPercentages = formType === FormTypes.WELCOME_MAT;

        // Don't update if we are moving or resizing
        if (isMoving || this.isResizing()) return;

        const newPosition = styleToPosition(style.toJS(), respectPercentages);

        if (formType === FormTypes.SMART_BAR) {
          if (this.container && isInlineComponent(nextProps)) {
            setTimeout(() => {
              if (!this.container || (this.container.offsetLeft === this.state.left && this.container.offsetTop === this.state.top)) return;
              // Adjust positioning
              const left = this.container.offsetLeft;
              const top = this.container.offsetTop;

              this.setState({ left, top });
              if (onChangeComponent) onChangeComponent(id, { style: positionToStyle({ left, top }) }, true);
            }, 0);
          }
        } else {
          this.setState(newPosition);
        }
      }

      componentDidMount() {
        const {
          isPreview,
          isFirstError,
          formType,
          onChangeComponent,
          id,
          type,
          action,
          selected,
        } = this.props;

        if (isPreview) {
          document.addEventListener('keydown', this.onKeyDown);
          document.addEventListener('keyup', this.onKeyUp);
        } else if (isFirstError) {
          // Simulate clicking the error tooltip to focus the field
          setTimeout(() => this.onClickErrorTooltip());
        }

        if (isPreview && selected) {
          this.updateComponentPosition(false, true);
        }

        // Set left and top of inline components
        if (formType === FormTypes.SMART_BAR && isInlineComponent(this.props)) {
          const left = this.container.offsetLeft;
          const top = this.container.offsetTop;
          this.setState({ left, top });
          if (onChangeComponent) onChangeComponent(id, { style: positionToStyle({ left, top }) }, true);
        }
      }

      componentWillUnmount() {
        const { isPreview } = this.props;

        if (!isPreview) return;
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('keyup', this.onKeyUp);
      }

      componentDidUpdate(prevProps, prevState) {
        const { selected, changedJSON } = this.props;
        const { width, height } = this.state;

        // Get reference from text elements
        const textEl = (this.componentEl) ? this.componentEl.textEl : null;

        if (selected && (prevState.width !== width || prevState.height !== height)) {
          this.updateComponentPosition(false, true);
        }

        if (selected && textEl && height !== textEl.clientHeight) {
          this.setState({ height: textEl.clientHeight });
        }

        // Check to see if newly selected
        if (selected && ((prevProps.selected !== selected) || changedJSON)) {
          this.updateComponentPosition(false, true);
        }
      }

      onClickErrorTooltip() {
        const { isPreview } = this.props;
        const { inputField } = this.state;
        if (!inputField || isPreview) return;

        inputField.focus();
      }

      onSaveInputField(inputField) {
        const { isPreview } = this.props;
        if (!inputField || isPreview) return;

        this.setState({ inputField });
      }

      onSavePosition(shouldSnap) {
        const { id, onChangeComponent, formType, type, getSnappedPosition, componentDidMove } = this.props;
        const { keyPress } = this.state;
        const respectPercentages = (formType === FormTypes.WELCOME_MAT);
        let position = getRoundedPosition(this.state, respectPercentages);

        if (shouldSnap && formType !== FormTypes.SMART_BAR)
          position = getSnappedPosition(_.extend({
            initialPosition: this.initialPosition,
            resizeCorner: this.resizeCorner,
            dragType: this.dragType,
          }, this.state), respectPercentages);

        if (formType === FormTypes.WELCOME_MAT && type === 'background') {
          position.top = 0;
          position.left = 0;
        }

        const positionStyle = positionToStyle(position, respectPercentages);
        const hasChangedPosition = !_.isEqual(this.initialPosition, position);

        if (formType !== FormTypes.SMART_BAR) this.setState(position);
        if (onChangeComponent && (hasChangedPosition || keyPress)) {
          onChangeComponent(id, { style: positionStyle }, null, true);
        }
        componentDidMove(position, this.container, false);
      }

      onKeyDown(e) {
        const { selected } = this.props;
        const { isMoving, isSnapping, isMoveLockedToAxis } = this.state;
        // Return if the component is not selected and is not moving.  We only want to move selected
        // components and we want to manipulate the movement type with alt and shift.
        if (!selected && !isMoving) return;
        if (['INPUT', 'TEXTAREA'].indexOf(document.activeElement.nodeName) > -1) {
          e.stopImmediatePropagation();
          return false;
        }

        switch (e.which || e.keyCode || 0) {
          // Shift
          case 16: {
            if (!isMoveLockedToAxis) this.setState({ isMoveLockedToAxis: true });
            break;
          }
          // Alt
          case 18: {
            if (isSnapping) this.setState({ isSnapping: false });
            break;
          }
          // Left
          case 37:
          // Up
          case 38:
          // Right
          case 39:
          // Down
          case 40: {
            this.setState({ keyPress: e.which || e.keyCode || 0 },
              this.onPressArrowKey(e.which || e.keyCode || 0, !!e.shiftKey),
            );
            break;
          }
        }
      }

      onKeyUp(e) {
        const { id, selected, onDeleteComponent } = this.props;
        const { isMoving, isSnapping, isMoveLockedToAxis } = this.state;
        // Return if the component is not selected and is not moving.  We only want to move selected
        // components and we want to manipulate the movement type with alt and shift.
        if (!selected && !isMoving) return;
        if (['INPUT', 'TEXTAREA'].indexOf(document.activeElement.nodeName) > -1) {
          e.stopImmediatePropagation();
          return false;
        }

        switch (e.which || e.keyCode || 0) {
          // Shift
          case 16: {
            if (isMoveLockedToAxis) this.setState({ isMoveLockedToAxis: false });
            break;
          }
          // Alt
          case 18: {
            if (!isSnapping) this.setState({ isSnapping: true });
            break;
          }
          // Left
          case 37:
          // Up
          case 38:
          // Right
          case 39:
          // Down
          case 40: {
            // We don't want arrow keys to do anything if we are moving.
            if (isMoving || this.isResizing()) return;
            this.onSavePosition(false);
            break;
          }
          // Delete
          case 46:
          case 8: {
            onDeleteComponent(id);
            break;
          }
        }
      }

      onPressArrowKey(pressedArrowKey, shiftKey) {
        const { moveIsDisabled } = this.props;
        const { isMoving } = this.state;
        // getRoundedPosition copies the object so we don't need to.
        let position = getRoundedPosition(this.state);
        const increment = shiftKey ? 10 : 1;

        // If we are moving or resizing we don't want to move the component with the arrow keys.
        if (isMoving || this.isResizing() || moveIsDisabled) return;

        switch (pressedArrowKey) {
          // Left
          case 37: {
            this.setState({ left: position.left - increment }, this.keyPressUpdatePosition(pressedArrowKey));
            break;
          }
          // Up
          case 38: {
            this.setState({ top: position.top - increment }, this.keyPressUpdatePosition(pressedArrowKey));
            break;
          }
          // Right
          case 39: {
            this.setState({ left: position.left + increment }, this.keyPressUpdatePosition(pressedArrowKey));
            break;
          }
          // Down
          case 40: {
            this.setState({ top: position.top + increment }, this.keyPressUpdatePosition(pressedArrowKey));
            break;
          }
        }
      }

      onStartDrag(type) {
        // Save drag type so we can get the correct snap function.
        this.dragType = type;
        this.setState({ keyPress: null });
        const stateCopy = _.clone(this.state) || {};
        this.initialPosition = _.pick(stateCopy, 'top', 'left', 'width', 'height');
      }

      onDragMoveHandle(ui) {
        const {
          id,
          popupScaleRatio,
          onMoveComponent,
          popupWidth,
          popupHeight,
          formType,
          type,
          action,
          moveIsDisabled,
          isPreview,
        } = this.props;

        if (moveIsDisabled === true || !isPreview) return;

        const {
          isMoving,
          width,
          height,
        } = this.state;

        const { initialX, initialY, x, y } = ui;
        const {
          left: initialLeft,
          top: initialTop,
        } = this.initialPosition;
        const deltaX = (x - initialX) / popupScaleRatio;
        const deltaY = (y - initialY) / popupScaleRatio;

        this.props.onMouseDrag(x, y);

        if (!isMoving) {
          this.setState({ isMoving: true });
          if (onMoveComponent) onMoveComponent(id);
        }

        if (!deltaX && !deltaY) return;

        let newTop = initialTop + deltaY;
        const newLeft = initialLeft + deltaX;

        this.setState({
          left: newLeft,
          top: newTop,
        });

        this.updateComponentPosition(true, false);

        if (this.debounceOnChangeComponent && formType === FormTypes.SMART_BAR) this.debounceOnChangeComponent(id, { style: positionToStyle({ left: newLeft, top: newTop }) }, null, true);
      }

      onDragSideHandle(side, ui) {
        const { id, popupScaleRatio, onResizeComponent } = this.props;
        const { isResizingSide } = this.state;
        const { initialX, initialY, x, y } = ui;
        const {
          left: initialLeft,
          top: initialTop,
          width: initialWidth,
          height: initialHeight,
        } = this.initialPosition;
        const deltaX = (x - initialX) / popupScaleRatio;
        const deltaY = (y - initialY) / popupScaleRatio;

        if (!isResizingSide) {
          this.setState({ isResizingSide: true });
          if (onResizeComponent) onResizeComponent(id);
        }

        // Both deltas are zero so don't do anything.
        if (!deltaX && !deltaY) return;

        this.props.onMouseDrag(x, y);

        let newPosition;
        switch (side) {
          case 'left': {
            newPosition = {
              left: initialLeft + deltaX,
              width: initialWidth - deltaX,
            };
            break;
          }
          case 'right': {
            newPosition = { width: initialWidth + deltaX };
            break;
          }
          case 'top': {
            newPosition = {
              top: initialTop + deltaY,
              height: initialHeight - deltaY,
            };
            break;
          }
          case 'bottom': {
            newPosition = { height: initialHeight + deltaY };
            break;
          }
        }

        if (newPosition) {
          this.setState(validatePosition(newPosition));
          this.updateComponentPosition(true, false);
        }
      }

      onDragCornerHandle(corner, ui) {
        const { id, popupScaleRatio, onResizeComponent } = this.props;
        const { isResizingCorner } = this.state;
        const { x, y } = ui;
        const {
          left: initialLeft,
          top: initialTop,
          width: initialWidth,
          height: initialHeight,
        } = this.initialPosition;

        this.resizeCorner = corner;
        const deltas = getCornerDragDeltas(corner, ui, {
          width: initialWidth,
          height: initialHeight,
        });

        if (!isResizingCorner) {
          this.setState({ isResizingCorner: true });
          if (onResizeComponent) onResizeComponent(id);
        }

        this.props.onMouseDrag(x, y);

        // Adjust for popup scale.
        deltas.x = deltas.x / popupScaleRatio;
        deltas.y = deltas.y / popupScaleRatio;

        let newPosition;
        switch (corner) {
          case 'top-left': {
            newPosition = {
              left: initialLeft - deltas.x,
              top: initialTop - deltas.y,
              width: initialWidth + deltas.x,
              height: initialHeight + deltas.y,
            };
            break;
          }
          case 'top-right': {
            newPosition = {
              top: initialTop - deltas.y,
              width: initialWidth + deltas.x,
              height: initialHeight + deltas.y,
            };
            break;
          }
          case 'bottom-left': {
            newPosition = {
              left : initialLeft - deltas.x,
              width: initialWidth + deltas.x,
              height: initialHeight + deltas.y,
            };
            break;
          }
          case 'bottom-right': {
            newPosition = {
              width: initialWidth + deltas.x,
              height: initialHeight + deltas.y,
            };
            break;
          }
        }

        if (newPosition) {
          this.setState(validatePosition(newPosition));
          this.updateComponentPosition(true, false);
        }
      }

      onStopDraggingHandle(e) {
        const {
          onMoveComponent,
          onResizeComponent,
          formType,
          getSnappedPosition,
          componentDidMove,
        } = this.props;
        const { isMoving } = this.state;
        const isResizing = this.isResizing();
        const shouldSnap = isMoving || isResizing;
        let dropResult;

        // FIXME: ideally position rounding, snapping, and saving should all be handled by popup
        // We are no longer moving or resizing the component.
        if (onMoveComponent) dropResult = onMoveComponent(null, e);
        if (onResizeComponent) onResizeComponent(null);

        if (formType === FormTypes.WELCOME_MAT && !isResizing) {
          if (dropResult.isValidMove === false) {
            const { left: initialX, top: initialY } = this.initialPosition;

            this.setState({
              left: initialX,
              top: initialY,
            });
          } else if (dropResult.isNewParent === false) {
            // Save position to state and store.  True enables snapping.
            this.onSavePosition(shouldSnap);
          }
        }

        // Update is dragging state so the component updates in componentWillReceiveProps() when
        // it receives the normalized position from popup json sanitization.
        if (formType !== FormTypes.WELCOME_MAT || dropResult.isNewParent === false) {
          this.setState({
            isMoving: false,
            isResizingSide: false,
            isResizingCorner: false,
          });
        }

        // Save position to state and store.  True enables snapping.
        if ((formType !== FormTypes.SMART_BAR && formType !== FormTypes.WELCOME_MAT) || isResizing) {
          this.onSavePosition(shouldSnap);
        } else {
          // Update ruler guide when selecting smart bar or WM
          componentDidMove(this.state, this.container, false);
        }

        // Unset resizeCorner after onSavePosition() since getSnappedPosition() needs
        // resizeCorner for snapping.
        this.resizeCorner = undefined;

        // Select component since we were just moving it.
        this.onSelectComponent();
      }

      saveHeight(container) {
        const { id, style, formType, type, onChangeComponent } = this.props;
        this.container = container;
        if (disableVerticalResize === false) {
          if (!this.container) return;
          const { height } = this.state;
          const newHeight = this.container.offsetHeight;

          if (newHeight !== height) {
            const heightStyle = { height: `${newHeight}px`};

            this.setState({ height: newHeight });
            if (onChangeComponent) onChangeComponent(id, { style: heightStyle });
          }
        }
      }

      onSaveAspectRatio(ratio) {
        const { id, onChangeComponent } = this.props;
        if (onChangeComponent) onChangeComponent(id, { defaultAspectRatio: ratio });
      }

      keyPressUpdatePosition(pressedArrowKey) {
        const { componentDidMove } = this.props;
        const { keyPress } = this.state;
        if (pressedArrowKey !== keyPress) {
          this.onSavePosition(false);
        }
        componentDidMove(this.state, this.container, true);
      }

      updateComponentPosition(isMoving, newlySelected) {
        const { getSnappedPosition, componentDidMove, formType } = this.props;
        const respectPercentages = (formType === FormTypes.WELCOME_MAT);
        let position = getRoundedPosition(this.state, respectPercentages);

        if (!newlySelected) {
          position = getSnappedPosition(_.extend({
            initialPosition: this.initialPosition,
            resizeCorner: this.resizeCorner,
            dragType: this.dragType,
          }, this.state));
        }

        componentDidMove(position, this.container, isMoving);
      }

      isResizing() {
        const { isResizingSide, isResizingCorner } = this.state;
        return isResizingSide || isResizingCorner;
      }

      renderFieldError() {
        const { error, formType, position } = this.props;
        if (!error) return;

        let className = 'listbuilder-popup-field-error';

        if (formType === FormTypes.SMART_BAR && position === 'top') {
          className += ' listbuilder-popup-field-error-bottom';
        }

        return (
          <div
            className={className}
            onClick={this.onClickErrorTooltip}
          >
            {error}
          </div>
        );
      }

      renderErrorOverlay() {
        const { errors } = this.props;

        if (!errors || !errors.keySeq().size) return;

        return <div className="sumome-react-wysiwyg-error-overlay" />
      }

      renderMoveHandle(content) {
        const { hovered, selected, type, action, formType, style, isPreview, moveIsDisabled  } = this.props;
        const { isMoving } = this.state;
        const moveHandleClass = classNames({
          'sumome-react-wysiwyg-show-handle': hovered || selected || isMoving,
          'sumome-react-wysiwyg-component-is-drag-disabled' : moveIsDisabled,
        });
        const newStyle = _.extend({}, style);

        return (
          <MoveHandle
            className={moveHandleClass}
            onStart={this.onStartDraggingMoveHandle}
            onDrag={this.onDragMoveHandle}
            onStop={this.onStopDraggingHandle}
            onMouseEnter={this.onMouseEnter}
            onMouseLeave={this.onMouseLeave}
            isDragDisabled={!isPreview}
            style={newStyle}
          >
            {content}
          </MoveHandle>
        );
      }

      renderResizeHandles() {
        const { selected, hidesVerticalHandles, hidesHorizontalHandles, isPreview } = this.props;
        if (!selected) return;

        const handles = [];

        _.each(HANDLES, handle => {
          const isVerticalHandle = VERTICAL_HANDLES.indexOf(handle) >= 0;
          const isHorizontalHandle = HORIZONTAL_HANDLES.indexOf(handle) >= 0;
          const isCornerHandle = CORNER_HANDLES.indexOf(handle) >= 0;

          if ((disableVerticalResize || hidesVerticalHandles) && isVerticalHandle) return;
          if ((disableHorizontalResize || hidesHorizontalHandles) && isHorizontalHandle) return;
          if ((disableCornerResize || hidesHorizontalHandles || hidesVerticalHandles) && isCornerHandle) return;

          const handlePascalCase = handleNameToPascalCase(handle);
          const onDragFunctionName = `onDrag${handlePascalCase}Handle`;
          const onStartDraggingFunctionName = `onStartDragging${handlePascalCase}Handle`;

          const handleProps = {
            key: handle,
            side: handle,
            onStart: this[onStartDraggingFunctionName],
            onDrag: this[onDragFunctionName],
            onStop: this.onStopDraggingHandle,
            isDragDisabled: !isPreview,
          };

          handles.push(<ResizeHandle {...handleProps} />);
        });

        return handles;
      }

      renderSizeTooltip() {
        if (!this.isResizing()) return;

        const { getSnappedPosition } = this.props;
        const position = getSnappedPosition(_.extend({
          initialPosition: this.initialPosition,
          resizeCorner: this.resizeCorner,
          dragType: this.dragType,
        }, this.state));
        const { width, height } = position;
        const sizeParts = [];

        if (width) sizeParts.push(`Width: ${width}px`);
        if (height) sizeParts.push(`Height: ${height}px`);
        if (!sizeParts.length) return;

        return (
          <div className="sumome-react-wysiwyg-component-size-tooltip">
            {sizeParts.join(' ')}
          </div>
        );
      }

      render() {
        // Copy so we can add stuff to the props before passing through.
        const propsCopy = _.clone(this.props);
        const {
          selected,
          hovered,
          formType,
          type,
          action,
          maxHeight,
          isValidDropTarget,
          isActiveDropTarget,
          isDragComponent,
          isDragComponentParent,
          isResizeComponent,
          isResizeComponentParent,
          footer,
          responsiveHiddenOn,
          getSnappedPosition,
        } = this.props;
        const { isMoving } = this.state;
        const isCloseButton = type === 'image' && ['close', 'optout'].indexOf(action) > -1;
        const isBackground = type === 'background';
        const respectPercentages = (formType === FormTypes.WELCOME_MAT);

        let position = getRoundedPosition(this.state, respectPercentages);
        if (isMoving || this.isResizing()) {
          position = getSnappedPosition(_.extend({
            initialPosition: this.initialPosition,
            resizeCorner: this.resizeCorner,
            dragType: this.dragType,
          }, this.state));
        }

        const positionStyle = positionToStyle(position, respectPercentages);

        // Style for the component wrapper. Clone so we can get height style if disabled and needed.
        let style = _.clone(positionStyle);

        // If the height is not controlled, we don't want to set it so we can save the rendered height.
        if (type !== 'background' || [FormTypes.WELCOME_MAT, FormTypes.SMART_BAR].indexOf(formType) === -1) {
          if (disableVerticalResize) delete style.height;
          if (disableHorizontalResize) delete style.width;
        }

        if (formType === FormTypes.SMART_BAR && type === 'textField') {
          style.height = 'auto';
        }

        const classOptions = {
          // Only show hovered if isn't selected and isn't moving.
          'sumome-react-wysiwyg-hovered': !selected && !isMoving && hovered,
          'sumome-react-wysiwyg-is-moving': (isMoving && isInlineComponent(propsCopy)),
          'sumome-react-wysiwyg-selected': selected || isMoving,
          'sumome-react-wysiwyg-valid-drop-target': isValidDropTarget,
          'sumome-react-wysiwyg-active-drop-target': isActiveDropTarget,
          'sumome-react-wysiwyg-outside-horizontal-resize-handles': position.width < RESIZE_HANDLE_SIZE * 2,
          'sumome-react-wysiwyg-outside-vertical-resize-handles': position.height < RESIZE_HANDLE_SIZE * 2,
          'sumome-react-wysiwyg-close-button': isCloseButton,
          'sumome-react-wysiwyg-is-drag-component-parent': isDragComponentParent,
          'sumome-react-wysiwyg-is-drag-component': isDragComponent,
          'sumome-react-wysiwyg-is-resize-component-parent': isResizeComponentParent,
          'sumome-react-wysiwyg-is-resize-component': isResizeComponent,
          [`sumome-react-wysiwyg-${formType}-${type}`]: true,
          [`sumome-react-wysiwyg-${type}`]: true,
        };

        if (this.props.className) {
          classOptions[this.props.className] = true;
        }

        if (formType === FormTypes.SMART_BAR && footer) {
          classOptions['sumome-react-wysiwyg-smart-bar-footer'] = true;
        }

        if (responsiveHiddenOn) {
          classOptions[getClassNameForResponsiveHiddenOn(responsiveHiddenOn)] = true;
        }

        style.position = 'absolute';

        // Copy style to js so we can modify it and then set it on propsCopy again.
        const styleCopy = propsCopy.style.toJS();

        // Provide a default if there isn't a box shadow.
        propsCopy.boxShadowStyle = {};
        if (styleCopy.boxShadow) {
          const boxShadows = styleCopy.boxShadow.split(', ');
          propsCopy.boxShadowStyle = {
            boxShadow: boxShadows[0],
            insetBoxShadow: boxShadows[1],
          };
        }

        if (styleCopy.display && styleCopy.display === 'block' && formType === FormTypes.SMART_BAR) {
          classOptions['sumome-react-wysiwyg-block-component'] = true;
        }

        const className = classNames(COMPONENT_CLASS_NAME, classOptions);

        if (styleCopy.gradient && styleCopy.gradient.length)  {
          styleCopy.background = `${styleCopy.gradient} ${styleCopy.backgroundColor}`;
          // Gradient is only a placeholder and not a style attr.
          styleCopy.gradient = undefined;
          // We append backgroundColor to the gradient so we don't need this.
          styleCopy.backgroundColor = undefined;
        }

        // isBrowserFont() technically takes a font face, but in the case of browser fonts
        // the font family is the font face as well.  We want to use the fallback for OS
        // compatibility so we swap out the font family.
        if (FontUtil.isBrowserFont(styleCopy.fontFamily)) {
          const fontOption = FontUtil.getFontOption(styleCopy.fontFamily);
          styleCopy.fontFamily = fontOption.family;
        }

        propsCopy.style = styleCopy;
        // The child doesn't need to know most position info.
        propsCopy.style = _.omit(propsCopy.style, 'position', 'top');
        _.extend(propsCopy.style, _.pick(positionStyle, 'width', 'height'));
        // Supply defaults for side border widths.
        propsCopy.style = _.extend({
          borderTopWidth: '0px',
          borderLeftWidth: '0px',
          borderBottomWidth: '0px',
          borderRightWidth: '0px',
        }, propsCopy.style);

        propsCopy.style = Immutable.Map(propsCopy.style);

        propsCopy.onSaveInputField = this.onSaveInputField;
        propsCopy.onSaveAspectRatio = this.onSaveAspectRatio;

        // Add handlers
        propsCopy.onFocus = this.onFocus;
        propsCopy.onBlur = this.onBlur;

        let placeholder;

        if (formType === FormTypes.SMART_BAR) {
          if (isCloseButton) {
            delete propsCopy.style.left;
            delete style.left;

            // Vertically center between 0 and maxHeight
            style.top = `${(maxHeight / 2) - (position.height / 2)}px`;
          }

          if (['button', 'dropdown', 'discount'].indexOf(type) > -1) {
            style.height = `${Math.max(maxHeight - 20, 32)}px`;
          }

          if (type === 'progressBar') {
            style.borderTopLeftRadius = propsCopy.style.get('borderTopLeftRadius');
            style.borderTopRightRadius = propsCopy.style.get('borderTopRightRadius');
            style.borderBottomRightRadius = propsCopy.style.get('borderBottomRightRadius');
            style.borderBottomLeftRadius = propsCopy.style.get('borderBottomLeftRadius');
          }

          if (isMoving) {
            placeholder = (
              <div
                className={className.replace('sumome-react-wysiwyg-is-moving', 'sumome-react-wysiwyg-component-placeholder')}
                style={style}
              >
                <ChildComponent
                  containerStyle={style}
                  {...propsCopy}
                />
              </div>
            );
          }
        }

        const component = (
          <div
            ref={this.saveHeight}
            className={className}
            style={style}
          >
            {this.renderMoveHandle(
              <ChildComponent
                ref={(el) => { this.componentEl = el; }}
                containerStyle={style}
                {...propsCopy}
              />
            )}
            {this.renderFieldError()}
            {this.renderErrorOverlay()}
            {this.renderResizeHandles()}
            {this.renderSizeTooltip()}
          </div>
        );

        if (formType === FormTypes.SMART_BAR) {
          return (
            <React.Fragment>
              {component}
              {placeholder}
            </React.Fragment>
          );
        }

        return component;
      }
    }
  };
});
